// @flow
import * as React from 'react';
import type { Element } from 'react';
import { useBreakpoint } from '@lib/hooks/useBreakpoint';
import { DETAILS_PACKAGE } from '@routes';
import { Link, withTranslation } from '@i18n';
import { SVGImageFixedAspectRatio } from '@components/SVG';
import { useRouter } from 'next/router';

import { ListingPriceIcon, ListingHospitalIcon, ListingLocationIcon } from '@images/svg/index';
import {
  StyledWrapper,
  StyledListItem,
  StyledTextContainer,
  StyledHeadline,
  StyledBookButton,
  StyledLink,
  StyledPackageIcon,
  StyledImageContainer,
  StyledPriceWrapper,
  StyledPrice,
  StyledActualPriceWrapper,
  StyledFinalPriceWrapper,
  StyledItemWrapper
} from './styles';

import type { Props } from './types';

const PackageSearchResultListItem = ({
  result, t, size, facilityPage
}: Props): Element<any> => {
  const isMobile = useBreakpoint('mobile', 'tablet');
  const router = useRouter();
  const isHome = router.asPath === '/en';

  const packageObject = result.attributes;

  return (
    <StyledWrapper size={size || { mobile: 1 / 2, tablet: 1 / 3, desktop: 1 / 4 }}>
      <StyledListItem facilityPage={facilityPage}>
        <StyledImageContainer facilityPage={facilityPage}>
          <Link href={DETAILS_PACKAGE} params={{ slug: packageObject.slug }} passHref>
            <StyledLink Image>
              <SVGImageFixedAspectRatio
                imageUrl={
                  packageObject.cover_image_url || '/images/svg/ProfilePackage/staticPackage.png'
                }
                imageAlt={packageObject.cover_image_alt_tag || ''}
                imageWidth="533"
                imageHeight="400"
              />
            </StyledLink>
          </Link>
        </StyledImageContainer>
        <StyledTextContainer>
          <StyledHeadline facilityPage={facilityPage}>
            <Link href={DETAILS_PACKAGE} params={{ slug: packageObject.slug }} passHref>
              <StyledLink packageObject>{packageObject?.name}</StyledLink>
            </Link>
          </StyledHeadline>
          <StyledItemWrapper style={isMobile && isHome && !facilityPage ? { display: 'none' } : {}} facilityPage={facilityPage}>
            <StyledPackageIcon facilityPage={facilityPage}>
              <ListingHospitalIcon />
            </StyledPackageIcon>
            {packageObject.facility_name}
          </StyledItemWrapper>
          <StyledItemWrapper facilityPage={facilityPage}>
            <StyledPackageIcon facilityPage={facilityPage}>
              <ListingLocationIcon />
            </StyledPackageIcon>
            {packageObject.city}
            ,
            {packageObject.country}
          </StyledItemWrapper>
          <StyledItemWrapper facilityPage={facilityPage}>
            <StyledPriceWrapper>
              <StyledPrice>
                <StyledPackageIcon facilityPage={facilityPage}>
                  <ListingPriceIcon />
                </StyledPackageIcon>
                <StyledActualPriceWrapper facilityPage={facilityPage}>
                  $
                  {packageObject.original_price}
                </StyledActualPriceWrapper>
                <StyledFinalPriceWrapper facilityPage={facilityPage}>
                  $
                  {packageObject.selling_price}
                </StyledFinalPriceWrapper>
              </StyledPrice>
              <svg width="100" height={isMobile ? '30' : '29'} viewBox="0 0 95 29">
                <rect x="6.28125" width="88.7177" height="29" rx="5" fill={facilityPage ? '#0F86BA' : '#57A1BB'} />
                <path
                  d="M0.00153717 14.5049L9.42084 6.27432L9.42494 22.7288L0.00153717 14.5049Z"
                  fill={facilityPage ? '#0F86BA' : '#57A1BB'}
                />
                <circle cx="11.5" cy="14.5859" r="2.5" fill="white" />
                <text
                  x="20"
                  y="50%"
                  dominantBaseline="middle"
                  fontSize="9px"
                  textAnchor="start"
                  fill="white"
                >
                  {t('Save', {
                    savingAmount: packageObject.saving_amount,
                    bookingPercentage: packageObject.booking_percentage
                  })}
                </text>
              </svg>
            </StyledPriceWrapper>
          </StyledItemWrapper>
        </StyledTextContainer>
        <Link href={DETAILS_PACKAGE} params={{ slug: packageObject.slug }} passHref>
          <StyledLink facilityPage={facilityPage}>
            <StyledBookButton facilityPage={facilityPage}>{t('BookButton')}</StyledBookButton>
          </StyledLink>
        </Link>
      </StyledListItem>
    </StyledWrapper>
  );
};

export default withTranslation('ProfilePackages')(PackageSearchResultListItem);
